import Link from 'next/link';

import ChevronRightIcon from '@/components/Icons/ChevronRight';
import { HeaderDropdowns } from '@/components/NewHeader/HeaderDropdowns';
import { LoginButton } from '@/components/NewHeader/LoginButton';
import { Search } from '@/components/NewHeader/Search';
import { Experiences } from '@/components/NewHeader/SideMenu/Experiences';
import { Guides } from '@/components/NewHeader/SideMenu/Guides';
import { getNavIcon } from '@/components/NewHeader/SideMenu/getNavIcon';
import { Offers } from '@/components/NewHeader/SideMenu/Offers';
import { TeamStatus } from '@/components/NewHeader/SideMenu/TeamStatus';
import type { SideMenuContent } from '@/components/NewHeader/SideMenu/useSideMenu';
import { useSideMenu } from '@/components/NewHeader/SideMenu/useSideMenu';
import { VillasInPuglia } from '@/components/NewHeader/SideMenu/VillasInPuglia';
import { VillasInSicily } from '@/components/NewHeader/SideMenu/VillasInSicily';
import { useTranslation } from '@/contexts/dictionaryContext';
import useGTMEvents from '@/hooks/useGTMEvents';
import useUserContext from '@/hooks/useUserContext';
import { cn } from '@/lib/utils';

export function SideMenuContent() {
  const {
    newHeader: { nav },
  } = useTranslation();
  const { currentContent, setCurrentContent, close } = useSideMenu();
  const { isLogged } = useUserContext();

  const { sendMenuClickEvent } = useGTMEvents();

  const handleCloseClick = (section: string) => {
    sendMenuClickEvent({ section, sectionItem: 'none' });
    close();
  };

  const handleSetCurrentContent = (id: string, section: string) => {
    sendMenuClickEvent({ section, sectionItem: 'none' });
    setCurrentContent(id as SideMenuContent);
  };

  const bottomNavIds = ['contacts', 'offers'];
  const orderedNav = [
    ...nav.filter((item) => !bottomNavIds.includes(item.id)),
    ...bottomNavIds
      .map((id) => nav.find((item) => item.id === id))
      .filter((item): item is (typeof nav)[number] => Boolean(item)),
  ];

  if (currentContent === 'initial') {
    return (
      <div className="flex flex-col px-3 pb-6">
        <div className="py-3">
          <Search inFlow />
        </div>

        <div className="flex flex-col uppercase border-t border-gray-200">
          {orderedNav.map((item) => {
            const isOffers = item.id === 'offers';

            if (item.href) {
              return (
                <Link
                  href={item.href}
                  key={item.id}
                  className="px-1 py-3 flex items-center gap-3 w-full border-b border-gray-200 text-graphite"
                  onClick={() => handleCloseClick(item.title)}>
                  {getNavIcon(item.id)}
                  <span className="flex-1 uppercase text-left">{item.title}</span>
                  <ChevronRightIcon className="size-5 shrink-0" />
                </Link>
              );
            }

            return (
              <button
                key={item.id}
                type="button"
                onClick={() => handleSetCurrentContent(item.id, item.title)}
                className={cn(
                  'px-1 py-3 flex items-center gap-3 w-full border-b border-gray-200 text-graphite',
                  'text-left',
                )}>
                {getNavIcon(item.id)}
                <span
                  className={cn('flex-1 uppercase', {
                    'text-primary': isOffers,
                  })}>
                  {item.title}
                </span>
                <ChevronRightIcon className="size-5 shrink-0" />
              </button>
            );
          })}
        </div>

        <div className="pt-5 space-y-4">
          <TeamStatus />

          <div className="flex justify-center items-center gap-2 relative">
            <HeaderDropdowns />
          </div>

          {!isLogged && <LoginButton variant="sideMenu" />}
        </div>
      </div>
    );
  }

  if (currentContent === 'offers') {
    return <Offers />;
  }

  if (currentContent === 'villas-in-sicily') {
    return <VillasInSicily />;
  }

  if (currentContent === 'villas-in-puglia') {
    return <VillasInPuglia />;
  }

  if (currentContent === 'experiences') {
    return <Experiences />;
  }

  if (currentContent === 'guides') {
    return <Guides />;
  }

  return null;
}
